import { useCallback, useMemo, useState } from 'react'

import IconLoading from '@/assets/icons/icon-loading.svg'
import { Button } from '@/components/ui/button.tsx'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { useDeleteList } from '@/modules/tasks/query/task-list-query'
import type { List } from '@/modules/tasks/service/task-list-service'
import { handleError } from '@/utils/error'

interface DeleteTaskDialog {
  list?: List
  show: boolean
  onVisibilityChange: (state: boolean) => void
  onDeleted?: () => void
}

export const DeleteListDialog = (props: DeleteTaskDialog) => {
  const { list, show, onDeleted, onVisibilityChange } = props
  const [isDeletingTasks, setIsDeletingTasks] = useState(false)

  const hideDialog = useCallback(
    () => onVisibilityChange(false),
    [onVisibilityChange],
  )

  const deleteList = useDeleteList()
  const toast = useToast()

  const showDeleteTasksCheckbox = useMemo(
    () => list?.tasks?.length !== 0,
    [list?.tasks?.length],
  )

  const handleDelete = useCallback(
    () => {
      if (!list) {
        return
      }

      deleteList
        .mutateAsync({
          id: list.id,
          contentPolicy: isDeletingTasks ? 'DELETE' : 'UNLINK',
        })
        .then(() => {
          toast.success('List deleted')
          setIsDeletingTasks(false)
          onDeleted?.()
          hideDialog()
        })
        .catch((error) => {
          handleError(error, 'Error deleting list')
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deleteList, hideDialog, list, toast],
  )

  function handleCheckedChange(checked: boolean) {
    setIsDeletingTasks(checked)
  }

  return (
    <Dialog show={show}>
      <Dialog.Header>
        <Dialog.Title>Are you sure?</Dialog.Title>
        <Dialog.Description className="text-center">
          This action cannot be undone. This will permanently remove the list
          &apos;{list?.name}&apos;
        </Dialog.Description>
      </Dialog.Header>
      <div>
        {showDeleteTasksCheckbox && (
          <label>
            <Checkbox
              checked={isDeletingTasks}
              onCheckedChange={handleCheckedChange}
            />
            <span className="ml-3 text-secondary-500 dark:text-secondary-200">
              Delete Tasks in the List
            </span>
          </label>
        )}
      </div>
      <Dialog.ClosableFooter
        className="mt-6"
        onClose={hideDialog}
      >
        <Button
          variant="destructive"
          onClick={handleDelete}
          className="flex items-center gap-2"
          disabled={deleteList.isLoading}
        >
          {deleteList.isLoading && (
            <IconLoading className="h-4 w-4 animate-spin" />
          )}
          Remove
        </Button>
      </Dialog.ClosableFooter>
    </Dialog>
  )
}
